"use client";

import * as React from "react";
import {
  Avatar as RadixAvatar,
  AvatarProps as RadixAvatarProps,
  Badge as RadixBadge,
  IconButton as RadixIconButton,
  Skeleton as RadixSkeleton,
  Text,
  TextField as RadixTextField,
  type BadgeProps,
  type ButtonProps as RadixButtonProps,
  type IconButtonProps as RadixIconButtonProps,
  type SkeletonProps,
  type TextProps,
  Button as RadixButton,
} from "@radix-ui/themes";
import type {
  GetPropDefTypes,
  avatarPropDefs,
  badgePropDefs,
  buttonPropDefs,
  dialogContentPropDefs,
  dropdownMenuContentPropDefs,
  dropdownMenuItemPropDefs,
  iconButtonPropDefs,
  textPropDefs,
  textFieldRootPropDefs,
  selectTriggerPropDefs,
  skeletonPropDefs,
} from "@radix-ui/themes/props";
import cx from "clsx";
import { useElement } from "./widgets-context.js";
import { getDomProps, namespaceClassNames } from "./utils.js";
import { EyeClosedIcon, EyeOpenIcon } from "@radix-ui/react-icons";
import { useTranslation } from "./i18n/use-translation.js";

export interface ButtonProps extends Omit<
  RadixButtonProps,
  "variant" | "color"
> {
  variant?: "primary" | "secondary" | "destructive";
  unsafe_radixVariant?: RadixButtonProps["variant"];
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { className, variant = "primary", unsafe_radixVariant, ...props },
    ref,
  ) {
    const element = useElement(`${variant}Button`);

    const themeProps = (() => {
      let props: RadixButtonProps = {};
      if (variant === "primary") {
        props = {
          variant: "solid",
        } satisfies RadixButtonProps;
      } else if (variant === "secondary") {
        props = {
          highContrast: true,
          variant: "surface",
          color: "gray",
        } satisfies RadixButtonProps;
      } else if (variant === "destructive") {
        props = {
          variant: "solid",
          color: "red",
        } satisfies RadixButtonProps;
      } else {
        variant satisfies never;
      }

      if (unsafe_radixVariant) {
        props.variant = unsafe_radixVariant;
      }

      return props;
    })();

    return (
      <RadixButton
        ref={ref}
        {...getDomProps({
          elementId: `${variant}Button`,
          className: cx(
            className,
            // TODO: Remove BEM-style selector once conventions are standardized
            `button--${variant}`,
          ),
        })}
        {...themeProps}
        {...props}
        {...element}
      />
    );
  },
);

export interface IconButtonProps extends RadixIconButtonProps {
  title: string;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton({ className, ...props }, ref) {
    const element = useElement("iconButton");
    return (
      <RadixIconButton
        ref={ref}
        {...getDomProps({ elementId: "iconButton", className })}
        variant="ghost"
        color="gray"
        aria-label={props.title}
        {...props}
        {...element}
      />
    );
  },
);

export const Skeleton = React.forwardRef<HTMLSpanElement, SkeletonProps>(
  function Skeleton({ className, ...props }, ref) {
    const element = useElement("skeleton");
    return (
      <RadixSkeleton
        ref={ref}
        {...getDomProps({ elementId: "skeleton", className })}
        {...props}
        {...element}
      />
    );
  },
);

export const Label = React.forwardRef<HTMLLabelElement, TextProps>(
  function Label({ children, className, style = {}, ...props }, ref) {
    const element = useElement("label");
    return (
      <Text
        as="label"
        ref={ref}
        weight="bold"
        size="2"
        {...getDomProps({ elementId: "label", className })}
        style={{ width: "fit-content", ...style }}
        // Text props are a complex union type depending on the `as` prop value,
        // which breaks down when spreading in `element`.
        {...(props as any)}
        {...element}
      >
        {children}
      </Text>
    );
  },
);

export const TextField = React.forwardRef<
  HTMLInputElement,
  RadixTextField.RootProps
>(function TextField({ className, ...props }, ref) {
  const element = useElement("textfield");
  return (
    <RadixTextField.Root
      ref={ref}
      variant="surface"
      className={cx(namespaceClassNames("text-field"), className)}
      {...props}
      {...element}
    />
  );
});

export const TextFieldSlot = React.forwardRef<
  HTMLDivElement,
  RadixTextField.SlotProps
>(function TextFieldSlot({ className, ...props }, ref) {
  return (
    <RadixTextField.Slot
      ref={ref}
      className={cx(namespaceClassNames("text-field-slot"), className)}
      {...props}
    />
  );
});

export const PasswordField = React.forwardRef<
  HTMLInputElement,
  RadixTextField.RootProps
>(function PasswordField({ className, ...props }, ref) {
  const element = useElement("textfield");
  const [showPassword, setShowPassword] = React.useState(false);
  const translate = useTranslation();
  const hidePasswordText = translate({
    defaultMessage: "Hide password",
    id: "Jv4Vps",
    description: "Button text to hide password",
  });
  const showPasswordText = translate({
    defaultMessage: "Show password",
    id: "UIfBSd",
    description: "Button text to show password",
  });
  return (
    <RadixTextField.Root
      ref={ref}
      variant="surface"
      className={cx(namespaceClassNames("text-field"), className)}
      {...props}
      {...element}
      type={showPassword ? "text" : "password"}
    >
      <RadixTextField.Slot side="right">
        <IconButton
          type="button"
          size="1"
          onClick={() => setShowPassword(!showPassword)}
          title={showPassword ? hidePasswordText : showPasswordText}
        >
          {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
        </IconButton>
      </RadixTextField.Slot>
    </RadixTextField.Root>
  );
});

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(
  function Badge({ className, ...props }, ref) {
    const element = useElement("badge");
    return (
      <RadixBadge
        ref={ref}
        {...getDomProps({ elementId: "badge", className })}
        {...props}
        {...element}
      />
    );
  },
);

interface AvatarProps extends RadixAvatarProps {
  dim?: boolean;
}

export const Avatar = React.forwardRef<HTMLImageElement, AvatarProps>(
  function Avatar({ dim, className, ...props }, ref) {
    const element = useElement("avatar");
    return (
      <RadixAvatar
        ref={ref}
        color="gray"
        {...getDomProps({ elementId: "avatar", className })}
        {...props}
        {...element}
        // TODO: use CSS var instead of hard-coded value for opacity
        style={dim ? { opacity: 0.6, ...props.style } : props.style}
      />
    );
  },
);

type OmitAsChild<T> = {
  [K in keyof T]: T[K] extends undefined
    ? undefined
    : Omit<NonNullable<T[K]>, "asChild">;
};

export * as Dialog from "./elements/dialog.js";
export * as AlertDialog from "./elements/alert-dialog.js";
export * as DropdownMenu from "./elements/dropdown-menu.js";
export * as Select from "./elements/select.js";

export type Elements = OmitAsChild<{
  dialog?: GetPropDefTypes<typeof dialogContentPropDefs>;
  primaryButton?: GetPropDefTypes<typeof buttonPropDefs>;
  secondaryButton?: GetPropDefTypes<typeof buttonPropDefs>;
  destructiveButton?: GetPropDefTypes<typeof buttonPropDefs>;
  iconButton?: GetPropDefTypes<typeof iconButtonPropDefs>;
  textfield?: GetPropDefTypes<typeof textFieldRootPropDefs>;
  select?: GetPropDefTypes<typeof selectTriggerPropDefs>;
  badge?: GetPropDefTypes<typeof badgePropDefs>;
  dropdown?: GetPropDefTypes<typeof dropdownMenuContentPropDefs>;
  primaryMenuItem?: GetPropDefTypes<typeof dropdownMenuItemPropDefs>;
  destructiveMenuItem?: GetPropDefTypes<typeof dropdownMenuItemPropDefs>;
  avatar?: Omit<GetPropDefTypes<typeof avatarPropDefs>, "fallback">;
  label?: GetPropDefTypes<typeof textPropDefs>;
  skeleton?: GetPropDefTypes<typeof skeletonPropDefs>;
}>;
